/**
 * Integer hashing primitives.
 *
 * Everything procedural in this project is derived from these functions rather
 * than from stateful permutation tables. That matters for two reasons:
 *
 *  1. Terrain chunks are generated in Web Workers. A worker has no shared state
 *     with the main thread, so the only way to guarantee identical results is
 *     for generation to be a pure function of (coordinate, seed).
 *  2. Adjacent chunks must produce bit-identical border vertices. Hash-driven
 *     noise sampled at *global planet coordinates* does that by construction.
 */

/** 32-bit integer avalanche (a variant of the murmur3 finalizer). */
export function hashU32(x: number): number {
  let h = x | 0;
  h ^= h >>> 16;
  h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13;
  h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

export function hash2i(x: number, y: number, seed: number): number {
  let h = seed | 0;
  h = Math.imul(h ^ (x | 0), 0x27d4eb2d);
  h = Math.imul(h ^ (y | 0), 0x165667b1);
  return hashU32(h);
}

export function hash3i(x: number, y: number, z: number, seed: number): number {
  let h = seed | 0;
  h = Math.imul(h ^ (x | 0), 0x27d4eb2d);
  h = Math.imul(h ^ (y | 0), 0x165667b1);
  h = Math.imul(h ^ (z | 0), 0x9e3779b1);
  return hashU32(h);
}

export function hash4i(x: number, y: number, z: number, w: number, seed: number): number {
  let h = seed | 0;
  h = Math.imul(h ^ (x | 0), 0x27d4eb2d);
  h = Math.imul(h ^ (y | 0), 0x165667b1);
  h = Math.imul(h ^ (z | 0), 0x9e3779b1);
  h = Math.imul(h ^ (w | 0), 0x85ebca6b);
  return hashU32(h);
}

/** Deterministically folds a string into a 32-bit seed (FNV-1a). */
export function hashString(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Mixes two seeds into a new independent seed. */
export function mixSeed(a: number, b: number): number {
  return hashU32(Math.imul(a | 0, 0x9e3779b1) ^ hashU32(b | 0));
}

/** Maps a 32-bit hash to [0, 1). */
export function toUnit(h: number): number {
  return (h >>> 8) / 0x01000000;
}

/** Maps a 32-bit hash to [-1, 1). */
export function toSigned(h: number): number {
  return toUnit(h) * 2 - 1;
}
